// =============================================================================
// Venue analytics dashboard — the app counterpart of the website Partner
// Dashboard's Analytics section (Tonyte_website/src/dresdenAnalyticsPanel.js).
//
// Same real data, same sources, nothing estimated:
// - Per-event taps / shares: eventAnalytics/{eventId}.detailTaps / .sharesCount
// - Per-event interested (current): count of activityInterests for the event
// - Last 24 Hours / Monthly Reach: partnerAnalytics/{hostId}/{hourly|daily}
//   buckets written by the aggregate* Cloud Functions, keyed by Europe/Berlin
//   hour/day (mirrors partnerEngagementStore.js's bucketKeysFor exactly) and
//   summed across every hostId among the venue's events.
// - Added to calendar: eventAnalytics/{eventId}.calendarAdds (one per person)
// Events: the venue's published, public events from listVenueEventsEu (same
// set as the venue profile); drafts are excluded everywhere. A partner's own
// taps/shares/interest on their events are excluded server-side. Refreshes silently every 2 minutes while
// the screen is focused and the app is in the foreground.
// Reachable only from the venue profile, for the venue's owner and admins.
// =============================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  AppState,
  GestureResponderEvent,
  LayoutChangeEvent,
  RefreshControl,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { runAfterInteractions } from '../utils/afterInteractions';
import Svg, { Line, Path, Text as SvgText } from 'react-native-svg';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useIsFocused } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import {
  collection,
  doc,
  documentId,
  getCountFromServer,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import TonyteBackground from '../components/TonyteBackground';
import { auth, FIRESTORE_COLLECTIONS, firestore, functionsEu, httpsCallable } from '../firebase';
import { useProfile } from '../hooks/useProfile';
import { buildPartnerVenueSummaries, useVenueVibes } from '../hooks/useVenueVibes';
import { normalizeActivity } from '../hooks/useFeed';
import { useI18n } from '../i18n';
import { Radius, Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/types';
import type { RootStackParamList, TonyteActivity } from '../types';
import { parseActivityDate } from '../utils/activityDates';
import { getResolvedEventId } from '../utils/eventIds';
import { PICKER_LOCALE_BY_LANGUAGE } from '../utils/pickerFieldDate';

type Props = NativeStackScreenProps<RootStackParamList, 'VenueAnalytics'>;

type Metrics = { taps: number; interested: number; shares: number };
type Point = Metrics & { axisLabel: string; readoutLabel: string };
type EventStatus = 'live' | 'upcoming' | 'completed' | 'cancelled';
type EventRow = {
  event: TonyteActivity;
  status: EventStatus;
  start: Date | null;
  taps: number;
  shares: number;
  calendarAdds: number;
  interested: number | null;
};
type SortMode = 'upcoming' | 'taps' | 'interested' | 'shares' | 'calendar';

const METRIC_KEYS: (keyof Metrics)[] = ['taps', 'interested', 'shares'];
const BERLIN = 'Europe/Berlin';
const AUTO_REFRESH_MS = 2 * 60 * 1000;
// Validated with the dataviz palette validator: dark surface #101722 and
// daylight surface #FFFCF6 (amber deepened for daylight contrast). Same
// hues as the website dashboard's --pea-* chart colors.
const SERIES_DARK = { taps: '#9085e9', interested: '#c98500', shares: '#3987e5' };
const SERIES_LIGHT = { taps: '#9085e9', interested: '#b57800', shares: '#3987e5' };
const SERIES_DASH = { taps: undefined, interested: '7 4', shares: '2 4' };

const pad2 = (n: number) => String(n).padStart(2, '0');
const emptyMetrics = (): Metrics => ({ taps: 0, interested: 0, shares: 0 });

// Mirrors functions/index.js getPartnerBucketKeys (Europe/Berlin, h23).
function berlinKeys(date: Date) {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: BERLIN, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23',
    }).formatToParts(date);
    const by = Object.fromEntries(parts.map(p => [p.type, p.value]));
    const dayKey = `${by.year}-${by.month}-${by.day}`;
    return { dayKey, hourKey: `${dayKey}-${by.hour}`, hour: by.hour, year: Number(by.year), month: Number(by.month) };
  } catch {
    // Engines without timeZone support: fall back to device time (the app's
    // audience is Berlin-based, so this matches in practice).
    const dayKey = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
    const hour = pad2(date.getHours());
    return { dayKey, hourKey: `${dayKey}-${hour}`, hour, year: date.getFullYear(), month: date.getMonth() + 1 };
  }
}

const metricsFrom = (raw: Record<string, unknown> | undefined): Metrics => ({
  taps: typeof raw?.taps === 'number' ? raw.taps : 0,
  interested: typeof raw?.interested === 'number' ? raw.interested : 0,
  shares: typeof raw?.shares === 'number' ? raw.shares : 0,
});

async function fetchBuckets(hostIds: string[], sub: 'hourly' | 'daily', startKey: string, endKey: string) {
  const merged = new Map<string, Metrics>();
  await Promise.all(hostIds.map(async hostId => {
    const snap = await getDocs(query(
      collection(firestore, 'partnerAnalytics', hostId, sub),
      where(documentId(), '>=', startKey),
      where(documentId(), '<=', endKey),
    ));
    snap.forEach(item => {
      const current = merged.get(item.id) ?? emptyMetrics();
      const add = metricsFrom(item.data());
      METRIC_KEYS.forEach(key => { current[key] += add[key]; });
      merged.set(item.id, current);
    });
  }));
  return merged;
}

function eventStatus(event: TonyteActivity, now: number): { status: EventStatus; start: Date | null } {
  const { startAt, endAt } = parseActivityDate(event);
  if (event.status === 'cancelled') return { status: 'cancelled', start: startAt };
  if (startAt && startAt.getTime() > now) return { status: 'upcoming', start: startAt };
  if (endAt && endAt.getTime() < now) return { status: 'completed', start: startAt };
  return { status: 'live', start: startAt };
}

function niceMax(value: number) {
  if (value <= 0) return 4;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 2.5, 5, 10]) {
    if (step * magnitude >= value) return step * magnitude;
  }
  return Math.ceil(value / magnitude) * magnitude;
}

function useAppearance() {
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  return { theme, appearance, series: theme.isDark ? SERIES_DARK : SERIES_LIGHT };
}

// ---------------------------------------------------------------------------
// Line chart: 2px lines, hairline grid, clean ticks, legend with totals, and a
// touch-drag crosshair readout (the touch equivalent of the website's hover).
// ---------------------------------------------------------------------------

const CHART_HEIGHT = 190;
const PLOT_TOP = 12;
const PLOT_BOTTOM = 24;
const AXIS_LEFT = 28;
const AXIS_RIGHT = 6;

function EngagementChart({
  title, subtitle, points, labelEvery, emptyText, headerExtra,
}: {
  title: string;
  subtitle: string;
  points: Point[];
  labelEvery: number;
  emptyText: string;
  headerExtra?: React.ReactNode;
}) {
  const { theme, appearance, series } = useAppearance();
  const [width, setWidth] = useState(0);
  const [active, setActive] = useState<number | null>(null);
  const totals = points.reduce((acc, p) => {
    METRIC_KEYS.forEach(key => { acc[key] += p[key]; });
    return acc;
  }, emptyMetrics());
  const hasData = METRIC_KEYS.some(key => totals[key] > 0);
  const max = niceMax(Math.max(0, ...points.flatMap(p => METRIC_KEYS.map(key => p[key]))));
  const plotW = Math.max(0, width - AXIS_LEFT - AXIS_RIGHT);
  const plotH = CHART_HEIGHT - PLOT_TOP - PLOT_BOTTOM;
  const x = (i: number) => AXIS_LEFT + (points.length <= 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const y = (v: number) => PLOT_TOP + plotH - (v / max) * plotH;
  const gridColor = theme.isDark ? 'rgba(255,255,255,0.08)' : theme.colors.divider;
  const axisColor = theme.isDark ? 'rgba(255,255,255,0.45)' : theme.colors.textMuted;

  const indexAt = (event: GestureResponderEvent) => {
    if (!points.length || plotW <= 0) return null;
    const rel = (event.nativeEvent.locationX - AXIS_LEFT) / plotW;
    return Math.min(points.length - 1, Math.max(0, Math.round(rel * (points.length - 1))));
  };
  const activePoint = active !== null ? points[active] : null;

  return (
    <View style={[styles.card, appearance.card]}>
      <View style={styles.chartHead}>
        <View style={styles.flex}>
          <Text style={[styles.cardTitle, appearance.textPrimary]}>{title}</Text>
          <Text style={[styles.cardSubtitle, appearance.textSecondary]}>{subtitle}</Text>
        </View>
        {headerExtra}
      </View>
      <View style={styles.legend}>
        {METRIC_KEYS.map(key => (
          <View key={key} style={styles.legendItem}>
            <View style={[styles.legendSwatch, { backgroundColor: series[key] }]} />
            <Text style={[styles.legendLabel, appearance.textSecondary]}>{LEGEND_LABELS[key]}</Text>
            <Text style={[styles.legendValue, appearance.textPrimary]}>{totals[key]}</Text>
          </View>
        ))}
      </View>
      <View style={[styles.readout, appearance.readout, !activePoint && styles.readoutIdle]}>
        {activePoint ? (
          <>
            <Text style={[styles.readoutLabel, appearance.textSecondary]}>{activePoint.readoutLabel}</Text>
            {METRIC_KEYS.map(key => (
              <Text key={key} style={[styles.readoutValue, appearance.textPrimary]}>
                {activePoint[key]} <Text style={[styles.readoutKey, appearance.textSecondary]}>{LEGEND_LABELS[key].toLowerCase()}</Text>
              </Text>
            ))}
          </>
        ) : (
          <Text style={[styles.readoutLabel, appearance.textSecondary]}>{hasData ? 'Touch and drag the chart to see exact values.' : emptyText}</Text>
        )}
      </View>
      <View
        onLayout={(event: LayoutChangeEvent) => setWidth(event.nativeEvent.layout.width)}
        onStartShouldSetResponder={() => true}
        onMoveShouldSetResponder={() => true}
        onResponderGrant={event => setActive(indexAt(event))}
        onResponderMove={event => setActive(indexAt(event))}
        onResponderRelease={() => setActive(null)}
        onResponderTerminate={() => setActive(null)}
        style={styles.chartArea}
      >
        {width > 0 ? (
          <Svg width={width} height={CHART_HEIGHT}>
            {[0, 0.5, 1].map(fraction => (
              <React.Fragment key={fraction}>
                <Line x1={AXIS_LEFT} x2={width - AXIS_RIGHT} y1={y(max * fraction)} y2={y(max * fraction)} stroke={gridColor} strokeWidth={1} />
                <SvgText x={AXIS_LEFT - 6} y={y(max * fraction) + 4} fontSize={10} fill={axisColor} textAnchor="end">
                  {String(Math.round(max * fraction))}
                </SvgText>
              </React.Fragment>
            ))}
            {points.map((p, i) => (i % labelEvery === 0 || i === points.length - 1 ? (
              <SvgText
                key={`x${i}`}
                x={x(i)}
                y={CHART_HEIGHT - 6}
                fontSize={10}
                fill={axisColor}
                // Edge labels anchor inward so they never clip at the plot edges.
                textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}
              >
                {p.axisLabel}
              </SvgText>
            ) : null))}
            {METRIC_KEYS.map(key => (
              <Path
                key={key}
                d={points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`).join(' ')}
                stroke={series[key]}
                strokeWidth={2}
                strokeDasharray={SERIES_DASH[key]}
                strokeLinejoin="round"
                strokeLinecap="round"
                fill="none"
              />
            ))}
            {active !== null ? (
              <Line x1={x(active)} x2={x(active)} y1={PLOT_TOP} y2={PLOT_TOP + plotH} stroke={axisColor} strokeWidth={1} />
            ) : null}
          </Svg>
        ) : null}
      </View>
    </View>
  );
}

const LEGEND_LABELS: Record<keyof Metrics, string> = { taps: 'Taps', interested: 'Interested', shares: 'Shares' };
const STATUS_LABELS: Record<EventStatus, string> = {
  live: 'Live', upcoming: 'Upcoming', completed: 'Completed', cancelled: 'Cancelled',
};
const SORT_OPTIONS: { key: SortMode; label: string }[] = [
  { key: 'upcoming', label: 'Upcoming first' },
  { key: 'taps', label: 'Most taps' },
  { key: 'interested', label: 'Most interested' },
  { key: 'shares', label: 'Most shared' },
  { key: 'calendar', label: 'Most calendar adds' },
];

export default function VenueAnalyticsScreen({ route, navigation }: Props) {
  const { venueId } = route.params;
  const insets = useSafeAreaInsets();
  const { language } = useI18n();
  const locale = PICKER_LOCALE_BY_LANGUAGE[language] ?? 'en-GB';
  const { theme, appearance } = useAppearance();
  const userId = auth.currentUser?.uid ?? null;
  const { profile } = useProfile(userId);
  const isAdmin = profile?.role === 'admin' || profile?.role === 'co-admin';
  const { events: publicEvents, venueProfiles } = useVenueVibes();
  const venue = useMemo(
    () => buildPartnerVenueSummaries(publicEvents, venueProfiles).find(item => item.id === venueId) ?? null,
    [publicEvents, venueId, venueProfiles],
  );
  const ownerUid = venue?.ownerUid;
  const isOwner = Boolean(userId) && ownerUid === userId;
  const canView = isOwner || isAdmin;

  const [rows, setRows] = useState<EventRow[]>([]);
  const [hours, setHours] = useState<Point[]>([]);
  const [days, setDays] = useState<Point[]>([]);
  const [month, setMonth] = useState(() => {
    const k = berlinKeys(new Date());
    return { year: k.year, month: k.month };
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortMode>('upcoming');

  const hostIds = useMemo(
    () => [...new Set([ownerUid, ...rows.map(row => row.event.hostId)].filter((id): id is string => Boolean(id) && id !== 'system'))],
    [ownerUid, rows],
  );

  const loadEvents = useCallback(async () => {
    if (!ownerUid || !canView) return;
    // Fresh from the server on every load/refresh: the venue's published,
    // public events, past and upcoming (functions/venueEvents.js). Drafts,
    // private, pending and cancelled listings never reach the dashboard, and
    // this is the same event set the venue profile shows.
    const listEvents = httpsCallable<{ venueId: string }, { events: ({ id: string } & Record<string, unknown>)[] }>(functionsEu, 'listVenueEventsEu');
    const { data } = await listEvents({ venueId });
    const byId = new Map<string, TonyteActivity>();
    data.events
      .map(item => normalizeActivity(item.id, item as never, 'activities'))
      .forEach(event => byId.set(getResolvedEventId(event), event));

    const now = Date.now();
    const loaded = await Promise.all([...byId.values()].map(async event => {
      const id = getResolvedEventId(event);
      const [analyticsSnap, interestSnap] = await Promise.all([
        getDoc(doc(firestore, 'eventAnalytics', id)).catch(() => null),
        getCountFromServer(query(collection(firestore, FIRESTORE_COLLECTIONS.activityInterests), where('activityId', '==', id))).catch(() => null),
      ]);
      const analytics = (analyticsSnap?.exists() ? analyticsSnap.data() : {}) as { detailTaps?: number; sharesCount?: number; calendarAdds?: number };
      return {
        event,
        ...eventStatus(event, now),
        taps: analytics.detailTaps ?? 0,
        shares: analytics.sharesCount ?? 0,
        calendarAdds: analytics.calendarAdds ?? 0,
        interested: interestSnap ? interestSnap.data().count : null,
      } satisfies EventRow;
    }));
    setRows(loaded);
  }, [canView, ownerUid, venueId]);

  const loadLast24 = useCallback(async (ids: string[]) => {
    const now = Date.now();
    const keyed = Array.from({ length: 24 }, (_, index) => berlinKeys(new Date(now - (23 - index) * 3600000)));
    const buckets = ids.length ? await fetchBuckets(ids, 'hourly', keyed[0].hourKey, keyed[23].hourKey) : new Map<string, Metrics>();
    setHours(keyed.map(k => ({
      axisLabel: `${k.hour}h`,
      readoutLabel: `${k.hour}:00`,
      ...(buckets.get(k.hourKey) ?? emptyMetrics()),
    })));
  }, []);

  const loadMonth = useCallback(async (ids: string[], year: number, monthNumber: number) => {
    const count = new Date(year, monthNumber, 0).getDate();
    const prefix = `${year}-${pad2(monthNumber)}`;
    const buckets = ids.length ? await fetchBuckets(ids, 'daily', `${prefix}-01`, `${prefix}-${pad2(count)}`) : new Map<string, Metrics>();
    setDays(Array.from({ length: count }, (_, index) => {
      const date = new Date(year, monthNumber - 1, index + 1);
      return {
        axisLabel: String(index + 1),
        readoutLabel: date.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' }),
        ...(buckets.get(`${prefix}-${pad2(index + 1)}`) ?? emptyMetrics()),
      };
    }));
  }, [locale]);

  const inFlight = useRef(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // mode: 'initial' shows the loader, 'pull' the pull-to-refresh spinner,
  // 'silent' (auto refresh) shows nothing and keeps current data on failure.
  const loadAll = useCallback(async (mode: 'initial' | 'pull' | 'silent' = 'initial') => {
    if (!canView || !ownerUid) {
      setLoading(false);
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    if (mode === 'initial') setLoading(true);
    if (mode === 'pull') setRefreshing(true);
    try {
      await loadEvents();
      setError(null);
      setRefreshTick(tick => tick + 1);
    } catch (err) {
      console.warn('[Tonyte][VenueAnalytics] event load failed', err);
      if (mode !== 'silent') setError('Could not load analytics. Pull down to try again.');
    } finally {
      inFlight.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  }, [canView, loadEvents, ownerUid]);

  useEffect(() => { void loadAll(); }, [loadAll]);

  // Auto refresh every 2 minutes, only while this screen is focused and the
  // app is in the foreground. Deferred until running animations/gestures
  // finish (InteractionManager) and skipped if a load is already running, so
  // it never competes with scrolling or chart dragging.
  const isFocused = useIsFocused();
  const [appActive, setAppActive] = useState(AppState.currentState === 'active');
  useEffect(() => {
    const subscription = AppState.addEventListener('change', state => setAppActive(state === 'active'));
    return () => subscription.remove();
  }, []);
  useEffect(() => {
    if (!isFocused || !appActive || !canView) return undefined;
    const timer = setInterval(() => {
      runAfterInteractions(() => { void loadAll('silent'); });
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [appActive, canView, isFocused, loadAll]);

  // Charts depend on the hostIds found among the loaded events.
  // refreshTick re-runs these after every successful (auto) refresh.
  useEffect(() => {
    if (!canView || !hostIds.length) return;
    loadLast24(hostIds).catch(err => console.warn('[Tonyte][VenueAnalytics] last 24h failed', err));
  }, [canView, hostIds, loadLast24, refreshTick]);
  useEffect(() => {
    if (!canView || !hostIds.length) return;
    loadMonth(hostIds, month.year, month.month).catch(err => console.warn('[Tonyte][VenueAnalytics] monthly failed', err));
  }, [canView, hostIds, loadMonth, month, refreshTick]);

  const current = berlinKeys(new Date());
  const atCurrentMonth = month.year === current.year && month.month === current.month;
  const shiftMonth = (delta: number) => setMonth(({ year, month: m }) => {
    const next = new Date(year, m - 1 + delta, 1);
    return { year: next.getFullYear(), month: next.getMonth() + 1 };
  });

  const counts = useMemo(() => {
    const by = (status: EventStatus) => rows.filter(row => row.status === status).length;
    const interestedKnown = rows.filter(row => row.interested !== null);
    return {
      published: rows.filter(row => row.status !== 'cancelled').length,
      upcoming: by('upcoming'),
      live: by('live'),
      completed: by('completed'),
      taps: rows.reduce((sum, row) => sum + row.taps, 0),
      shares: rows.reduce((sum, row) => sum + row.shares, 0),
      calendarAdds: rows.reduce((sum, row) => sum + row.calendarAdds, 0),
      interested: interestedKnown.length ? interestedKnown.reduce((sum, row) => sum + (row.interested ?? 0), 0) : null,
    };
  }, [rows]);

  const sortedRows = useMemo(() => {
    const list = [...rows];
    if (sort === 'taps') return list.sort((a, b) => b.taps - a.taps);
    if (sort === 'shares') return list.sort((a, b) => b.shares - a.shares);
    if (sort === 'calendar') return list.sort((a, b) => b.calendarAdds - a.calendarAdds);
    if (sort === 'interested') return list.sort((a, b) => (b.interested ?? -1) - (a.interested ?? -1));
    const now = Date.now();
    return list.sort((a, b) => {
      const at = a.start?.getTime() ?? -Infinity;
      const bt = b.start?.getTime() ?? -Infinity;
      const af = at >= now;
      const bf = bt >= now;
      if (af !== bf) return af ? -1 : 1;
      return af ? at - bt : bt - at;
    });
  }, [rows, sort]);

  const icon = theme.isDark ? '#fff' : theme.colors.icon;
  const metricCards: { label: string; value: number | null }[] = [
    { label: 'Published events', value: counts.published },
    { label: 'Upcoming', value: counts.upcoming },
    { label: 'Live now', value: counts.live },
    { label: 'Completed', value: counts.completed },
    { label: 'Followers', value: venue?.followersCount ?? 0 },
    { label: 'Total taps', value: counts.taps },
    { label: 'Interested', value: counts.interested },
    { label: 'Shares', value: counts.shares },
    { label: 'Added to calendar', value: counts.calendarAdds },
  ];

  return (
    <TonyteBackground appearanceAware>
      <StatusBar barStyle={theme.statusBarStyle} translucent backgroundColor="transparent" />
      <ScrollView
        contentContainerStyle={[styles.scroll, { paddingTop: insets.top + 12, paddingBottom: insets.bottom + 40 }]}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => void loadAll('pull')} tintColor={theme.isDark ? '#fff' : theme.colors.accent} />}
      >
        <View style={styles.header}>
          <TouchableOpacity style={[styles.backButton, appearance.control]} onPress={() => navigation.goBack()}>
            <Icon name="chevron-left" size={22} color={icon} />
          </TouchableOpacity>
          <View style={styles.flex}>
            <Text style={[styles.kicker, appearance.kicker]}>PARTNER DASHBOARD</Text>
            <Text style={[styles.title, appearance.textPrimary]} numberOfLines={1}>{venue?.name || 'Analytics'}</Text>
          </View>
        </View>

        {!canView ? (
          <Text style={[styles.message, appearance.textSecondary]}>
            {venue ? 'Only this venue’s owner and Tonyte admins can view its analytics.' : 'Loading venue…'}
          </Text>
        ) : loading ? (
          <ActivityIndicator style={styles.loader} color={theme.isDark ? '#fff' : theme.colors.accent} />
        ) : (
          <>
            {error ? <Text style={[styles.message, appearance.warning]}>{error}</Text> : null}
            <View style={styles.metricGrid}>
              {metricCards.map(card => (
                <View key={card.label} style={[styles.metricCard, appearance.card]}>
                  <Text style={[styles.metricValue, appearance.textPrimary]}>{card.value ?? '—'}</Text>
                  <Text style={[styles.metricLabel, appearance.textSecondary]} numberOfLines={1}>{card.label}</Text>
                </View>
              ))}
            </View>

            <EngagementChart
              title="Last 24 Hours"
              subtitle="Taps, interested actions and shares by hour."
              points={hours}
              labelEvery={6}
              emptyText="No engagement recorded in the last 24 hours."
            />

            <EngagementChart
              title="Monthly Reach"
              subtitle="Taps, interested actions and shares by day."
              points={days}
              labelEvery={5}
              emptyText="No engagement recorded this month."
              headerExtra={(
                <View style={styles.monthNav}>
                  <TouchableOpacity onPress={() => shiftMonth(-1)} style={[styles.monthButton, appearance.control]}>
                    <Icon name="chevron-left" size={16} color={icon} />
                  </TouchableOpacity>
                  <Text style={[styles.monthLabel, appearance.textPrimary]}>
                    {new Date(month.year, month.month - 1, 1).toLocaleDateString(locale, { month: 'short', year: 'numeric' })}
                  </Text>
                  <TouchableOpacity
                    onPress={() => shiftMonth(1)}
                    disabled={atCurrentMonth}
                    style={[styles.monthButton, appearance.control, atCurrentMonth && styles.disabled]}
                  >
                    <Icon name="chevron-right" size={16} color={icon} />
                  </TouchableOpacity>
                </View>
              )}
            />

            <View style={[styles.card, appearance.card]}>
              <Text style={[styles.cardTitle, appearance.textPrimary]}>Event performance</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.sortRow}>
                {SORT_OPTIONS.map(option => (
                  <TouchableOpacity
                    key={option.key}
                    onPress={() => setSort(option.key)}
                    style={[styles.sortChip, appearance.chip, sort === option.key && styles.sortChipActive, sort === option.key && appearance.chipActive]}
                  >
                    <Text style={[styles.sortText, appearance.textSecondary, sort === option.key && styles.sortTextActive, sort === option.key && appearance.textPrimary]}>{option.label}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
              {sortedRows.length ? sortedRows.map(row => (
                <TouchableOpacity
                  key={getResolvedEventId(row.event)}
                  style={[styles.eventRow, appearance.divider]}
                  onPress={() => navigation.navigate('EventDetail', { eventId: getResolvedEventId(row.event), event: row.event })}
                >
                  <View style={styles.flex}>
                    <Text style={[styles.eventTitle, appearance.textPrimary]} numberOfLines={1}>{row.event.title}</Text>
                    <Text style={[styles.eventMeta, appearance.textSecondary]} numberOfLines={1}>
                      {[row.start?.toLocaleDateString(locale, { day: 'numeric', month: 'short' }), STATUS_LABELS[row.status]].filter(Boolean).join(' · ')}
                    </Text>
                  </View>
                  <View style={styles.eventStats}>
                    <Text style={[styles.eventStat, appearance.textPrimary]}>{row.taps}<Text style={[styles.eventStatKey, appearance.textSecondary]}> taps</Text></Text>
                    <Text style={[styles.eventStat, appearance.textPrimary]}>{row.interested ?? '—'}<Text style={[styles.eventStatKey, appearance.textSecondary]}> int.</Text></Text>
                    <Text style={[styles.eventStat, appearance.textPrimary]}>{row.shares}<Text style={[styles.eventStatKey, appearance.textSecondary]}> shares</Text></Text>
                    <Text style={[styles.eventStat, appearance.textPrimary]}>{row.calendarAdds}<Text style={[styles.eventStatKey, appearance.textSecondary]}> cal.</Text></Text>
                  </View>
                </TouchableOpacity>
              )) : <Text style={[styles.message, appearance.textSecondary]}>No events for this venue yet.</Text>}
            </View>
          </>
        )}
      </ScrollView>
    </TonyteBackground>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1, minWidth: 0 },
  scroll: { paddingHorizontal: 16, gap: 14 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 4 },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  kicker: { color: '#FF9A3C', fontSize: 11, fontWeight: Typography.extrabold, letterSpacing: 1.6 },
  title: { color: '#fff', fontSize: 24, fontWeight: Typography.extrabold, marginTop: 2 },
  loader: { marginTop: 60 },
  message: { color: 'rgba(255,255,255,0.62)', fontSize: 13, lineHeight: 19, textAlign: 'center', paddingVertical: 16 },
  metricGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  metricCard: {
    width: '31.8%',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderRadius: 14,
    alignItems: 'center',
    backgroundColor: '#101722',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  metricValue: { color: '#fff', fontSize: 20, fontWeight: Typography.extrabold },
  metricLabel: { color: 'rgba(255,255,255,0.58)', fontSize: 10, marginTop: 3 },
  card: {
    borderRadius: 18,
    padding: 14,
    backgroundColor: '#101722',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  cardTitle: { color: '#fff', fontSize: 16, fontWeight: Typography.extrabold },
  cardSubtitle: { color: 'rgba(255,255,255,0.58)', fontSize: 12, marginTop: 2 },
  chartHead: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  legend: { flexDirection: 'row', flexWrap: 'wrap', gap: 14, marginTop: 12 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  legendSwatch: { width: 14, height: 3, borderRadius: 2 },
  legendLabel: { color: 'rgba(255,255,255,0.62)', fontSize: 12 },
  legendValue: { color: '#fff', fontSize: 12, fontWeight: Typography.extrabold },
  readout: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 10,
    minHeight: 32,
    marginTop: 10,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  readoutIdle: { opacity: 0.85 },
  readoutLabel: { color: 'rgba(255,255,255,0.6)', fontSize: 12 },
  readoutValue: { color: '#fff', fontSize: 13, fontWeight: Typography.extrabold },
  readoutKey: { color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: Typography.medium },
  chartArea: { marginTop: 6, height: CHART_HEIGHT },
  monthNav: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  monthButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  monthLabel: { color: '#fff', fontSize: 12, fontWeight: Typography.bold, minWidth: 64, textAlign: 'center' },
  disabled: { opacity: 0.35 },
  sortRow: { gap: 8, paddingVertical: 12 },
  sortChip: {
    height: 32,
    paddingHorizontal: 12,
    borderRadius: Radius.pill,
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  sortChipActive: { backgroundColor: 'rgba(255,122,0,0.18)', borderColor: 'rgba(255,154,60,0.45)' },
  sortText: { color: 'rgba(255,255,255,0.65)', fontSize: 12, fontWeight: Typography.bold },
  sortTextActive: { color: '#fff' },
  eventRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 11,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(255,255,255,0.12)',
  },
  eventTitle: { color: '#fff', fontSize: 14, fontWeight: Typography.bold },
  eventMeta: { color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 2 },
  eventStats: { alignItems: 'flex-end', gap: 1 },
  eventStat: { color: '#fff', fontSize: 12, fontWeight: Typography.extrabold },
  eventStatKey: { color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: Typography.medium },
});

// Midnight stays the canonical StyleSheet above; Daylight only overrides.
const createThemeStyles = (theme: TonyteTheme) => {
  const c = theme.colors;
  return StyleSheet.create({
    card: daylightStyle(theme, { backgroundColor: c.cardBackground, borderColor: c.cardBorder, ...theme.shadows.card }),
    control: daylightStyle(theme, { backgroundColor: c.controlBackground, borderColor: c.border }),
    chip: daylightStyle(theme, { backgroundColor: c.chipBackground, borderColor: c.border }),
    chipActive: daylightStyle(theme, { backgroundColor: c.chipBackgroundActive, borderColor: 'rgba(242,125,38,0.36)' }),
    readout: daylightStyle(theme, { backgroundColor: c.surface }),
    divider: daylightStyle(theme, { borderTopColor: c.divider }),
    kicker: daylightStyle(theme, { color: c.filterActiveIcon }),
    textPrimary: daylightStyle(theme, { color: c.textPrimary }),
    textSecondary: daylightStyle(theme, { color: c.textSecondary }),
    warning: daylightStyle(theme, { color: c.warning }),
  });
};
